/**
 * Плитка-папка модели: обложка — первое фото, на ней число фото; клик открывает папку.
 * В выдаче поиска по фото первое фото — лучшее совпавшее, `score` — его сходство.
 * Папку можно выделить (галочка, Ctrl/Meta/Shift-клик или режим выделения) и тегировать:
 * выделение и теги папки — это выделение и теги её обложки; у папки без обложки выделения нет.
 * `index` — место обложки в списке выделения (`folderLevelShown`).
 */
import { Check } from "lucide-react";

import { TileTags } from "@/components/library/TileTags";
import { RetryImg } from "@/components/trends/PinGrid";
import type { ReferenceItem } from "@/lib/library";
import { num, thumbUrl } from "@/lib/media";
import type { ModelFolder } from "@/lib/models";
import { scoreLabel } from "@/lib/similar";

export function ModelFolderTile({
  folder,
  onOpen,
  score,
  percent = true,
  index,
  selected,
  selecting,
  onToggle,
  tagNames,
}: {
  folder: ModelFolder<ReferenceItem>;
  onOpen: () => void;
  score?: number | undefined;
  /** `false` — бейдж процента не рисуется (выдача по описанию). */
  percent?: boolean;
  index: number;
  selected: ReadonlySet<number>;
  selecting: boolean;
  onToggle: (index: number, range: boolean) => void;
  tagNames: ReadonlyMap<number, string>;
}) {
  const cover = folder.items[0];
  const ratio = cover?.asset_width && cover.asset_height ? `${cover.asset_width} / ${cover.asset_height}` : "3 / 4";
  const isSelected = !!cover && selected.has(cover.id);
  const boxVisible = isSelected || selecting || selected.size > 0;
  return (
    <figure className="grid min-w-0 content-start gap-1" data-annot="папка модели">
      <div className="group relative">
        <button
          type="button"
          onClick={(e) => {
            if (cover && (selecting || e.ctrlKey || e.metaKey || e.shiftKey)) onToggle(index, e.shiftKey);
            else onOpen();
          }}
          // Shift-клик иначе выделяет текст страницы от прошлого клика до этого.
          onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
          className={`relative block max-h-[360px] w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle text-left focus-visible:outline-2 focus-visible:outline-accent ${
            isSelected ? "outline-2 outline-offset-[-2px] outline-accent" : ""
          }`}
          style={{ aspectRatio: ratio }}
          title="Открыть папку модели (Ctrl/Shift — выделить)"
          aria-label={`${folder.name}: ${num(folder.items.length)} фото`}
        >
          {cover ? (
            <RetryImg
              src={thumbUrl(cover.asset_path)}
              alt={folder.name}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          {percent && score !== undefined ? (
            <span
              className="absolute top-1.5 left-1.5 rounded-[4px] bg-black/60 px-1.5 text-[11px] leading-[18px] text-white tnum"
              title="Сходство лучшего фото модели с запросом"
            >
              {scoreLabel(score)}
            </span>
          ) : null}
          <span className="absolute right-1.5 bottom-1.5 rounded-[4px] bg-black/60 px-1.5 text-[11px] leading-[18px] text-white tnum">
            {num(folder.items.length)}
          </span>
        </button>
        {cover ? (
          <button
            type="button"
            aria-pressed={isSelected}
            aria-label={isSelected ? "Снять выделение" : "Выделить"}
            title="Выделить папку (Shift — диапазон)"
            onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(index, e.shiftKey);
            }}
            className={`absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-[4px] border transition-opacity ${
              isSelected ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white hover:bg-black/65"
            } ${boxVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"}`}
          >
            {isSelected ? <Check size={13} strokeWidth={3} /> : null}
          </button>
        ) : null}
      </div>
      <figcaption className="grid min-w-0 gap-0.5 px-0.5">
        <span className="truncate font-medium" title={folder.name}>
          {folder.name}
        </span>
        {folder.collection ? (
          <span className="truncate text-[11.5px] text-fg-faint" title={folder.collection}>
            {folder.collection}
          </span>
        ) : null}
        {cover ? <TileTags tagIds={cover.tag_ids} tagNames={tagNames} autoIds={cover.auto_tag_ids} /> : null}
      </figcaption>
    </figure>
  );
}
